/**
 * Updates — ADR-099 D8: the operator's clear "Core Updates" surface.
 *
 * Shows this fleet's core-currency status (deployed vs latest core SHA,
 * commits behind, changelog) computed by the console (D7) and relayed by
 * GET /api/core-status, plus the "Accept & apply core update" action —
 * fleet_owner-gated, reusing the SAME POST /api/deploy/apply primitive (and
 * confirm/in-flight/success/error pattern) the P3.3 Fleet-Health card
 * introduced. That card is now a slim pointer to this page — see FleetHealth.tsx.
 *
 * ADR-099 D8.3: BOTH halves of an apply — the agent-host redeploy and the
 * Mission Control container redeploy — are now dispatched by the console,
 * and neither one can fail the whole request on its own. So `agent_host`
 * gets the exact same "needs authorization" amber treatment `mission_control`
 * already had, instead of only ever showing a green success line.
 *
 * Status comes from the shared CoreStatusContext poll (AppShell also reads
 * it, for the nav badge/banner); this page refreshes it once more on mount
 * for a just-opened-the-page-accurate read, and again after a successful
 * apply dispatch.
 */
import { useCallback, useEffect, useState } from 'react'
import { api, ForbiddenError, UnauthorizedError, type ApplyCoreUpdateResult } from '../api/client'
import { useCoreStatus } from '../context/CoreStatusContext'
import { useAuth } from '../context/AuthContext'
import { relativeTime } from '../utils/relativeTime'
import { ReadyWorklist } from '../components/ReadyWorklist'
import { notifyPendingChangesChanged } from '../hooks/usePendingChangeSignal'

/** Extracts {status, message} from the "HTTP <status>: <json-body>" errors thrown
 * by the api client — same convention as FleetHealth/ImprovementPRReview/InvestigationDetail. */
function parseApiError(err: unknown, fallback: string): { status: number | null; message: string } {
  if (!(err instanceof Error)) return { status: null, message: fallback }
  const m = /^HTTP (\d+): ([\s\S]*)$/.exec(err.message)
  if (!m) return { status: null, message: err.message || fallback }
  const status = Number(m[1])
  let message = fallback
  try {
    const body = JSON.parse(m[2]) as { error?: string; detail?: string }
    message = body.detail || body.error || fallback
  } catch {
    /* not JSON */
  }
  return { status, message }
}

function shortSha(sha: string | undefined): string {
  if (!sha) return '—'
  return sha.slice(0, 7)
}

export function Updates() {
  const { user } = useAuth()
  const isOwner = user.fleet_role === 'fleet_owner'
  const { status, loading, loaded, refresh } = useCoreStatus()
  const hold = status?.hold
  const isFrozen = hold?.state === 'frozen'
  const handleRefresh = useCallback(() => {
    refresh()
    notifyPendingChangesChanged()
  }, [refresh])

  const [applying, setApplying] = useState(false)
  const [applyResult, setApplyResult] = useState<ApplyCoreUpdateResult | null>(null)
  const [applyError, setApplyError] = useState<string | null>(null)

  // Agent-host runtime SHA — a SEPARATE track from the MC-image status above
  // (agents auto-deploy; MC is operator-gated via "Accept & apply" below, so
  // the two legitimately differ). Sourced from GET /api/fleet/health, which
  // forwards the agent-host's own /internal/health verbatim (ADR-105 D2
  // .code_sha — the same field scripts/deploy-fleet.sh's deploy-lag guard
  // reads). Degrades to "unknown" on any failure — never errors the page.
  const [agentRuntimeSha, setAgentRuntimeSha] = useState<string | null>(null)
  const [agentRuntimeUnavailable, setAgentRuntimeUnavailable] = useState(false)

  useEffect(() => {
    let cancelled = false
    api.getFleetHealth()
      .then(health => {
        if (cancelled) return
        setAgentRuntimeSha(health.code_sha ?? null)
        setAgentRuntimeUnavailable(false)
      })
      .catch(() => {
        if (cancelled) return
        setAgentRuntimeSha(null)
        setAgentRuntimeUnavailable(true)
      })
    return () => { cancelled = true }
  }, [])

  const handleApply = useCallback(async () => {
    if (!window.confirm(
      "Redeploy this fleet from core main? This recomposes the fleet's SOUL/skills " +
      'and refreshes its base image from the latest core, and redeploys this ' +
      "fleet's Mission Control.",
    )) {
      return
    }
    setApplying(true)
    setApplyError(null)
    setApplyResult(null)
    try {
      const res = await api.applyCoreUpdate()
      setApplyResult(res)
      refresh()
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      if (err instanceof ForbiddenError) {
        setApplyError('Permission denied — fleet owner required.')
      } else {
        const { message } = parseApiError(err, 'Could not dispatch the update — try again.')
        setApplyError(message)
      }
    } finally {
      setApplying(false)
    }
  }, [refresh])

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Updates</h1>
          <p className="text-sm text-gray-500 mt-1">
            What is waiting to go live in this fleet, and whether the fleet is current with the platform.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          className="shrink-0 px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* ADR-122 D2/D8b — this fleet's Changes and the Platform update, named,
          with one Take live for the whole ready range. Deliberately above the platform-currency
          card below: this is the operator's worklist and the reason they opened
          the page, whereas core currency is a mostly-automatic track they only
          act on occasionally. Renders nothing when there is nothing to say. */}
      <ReadyWorklist />

      <div className="pt-2">
        <h2 className="text-lg font-semibold text-gray-900">Platform updates</h2>
        <p className="text-sm text-gray-500 mt-1">
          Whether this fleet is current with core <span className="font-mono">main</span>, and what a
          pending update brings (ADR-099).
        </p>
      </div>

      {/* Loading — first fetch still in flight. */}
      {!loaded && loading && (
        <div className="text-sm text-gray-400 animate-pulse">Checking core status...</div>
      )}

      {loaded && hold && isFrozen && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-semibold">Deployments are frozen</p>
          {hold.reason && <p className="mt-1">{hold.reason}</p>}
          {(hold.set_by || hold.set_at) && (
            <p className="mt-1 text-xs">
              {hold.set_by && <>Set by {hold.set_by}</>}
              {hold.set_by && hold.set_at && ' · '}
              {hold.set_at && (
                <>Set {relativeTime(hold.set_at) === 'just now' ? 'less than a minute ago' : relativeTime(hold.set_at)}</>
              )}
            </p>
          )}
        </div>
      )}
      {loaded && hold?.state === 'pinned' && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Pinned to core {shortSha(hold.core_sha)}
        </div>
      )}
      {loaded && hold?.state === 'unknown' && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Could not read this fleet's deploy hold.
        </div>
      )}

      {/* Fail-soft: the console couldn't be reached (or FLEET_KEY isn't
          configured for this fleet's MC yet) — never a crash, never a false
          "up to date." */}
      {loaded && status?.available !== true && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          Status unavailable{status?.reason ? ` — ${status.reason}` : ' — could not reach the console right now.'}
          {' '}Try refreshing in a moment.
        </div>
      )}

      {loaded && status?.available === true && (
        <>
          {/* Status line. NOTE: this whole card tracks the fleet's Mission
              Control IMAGE only — see the explanatory subtext below. It is a
              DIFFERENT track from "Agent runtime" further down (agents
              auto-deploy from core continuously; MC only updates when an
              owner clicks "Accept & apply" below — the two legitimately
              differ). */}
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            {status.update_available ? (
              <>
                <p className="text-sm font-semibold text-indigo-700">
                  <span aria-hidden="true">⬆</span> MC update available — {status.commits_behind} commit
                  {status.commits_behind === 1 ? '' : 's'} behind core (
                  <span className="font-mono">{shortSha(status.deployed_sha)}</span> →{' '}
                  <span className="font-mono">{shortSha(status.latest_sha)}</span>)
                </p>
              </>
            ) : (
              <p className="text-sm font-semibold text-green-700">
                <span aria-hidden="true">✓</span> MC is up to date with core (MC image{' '}
                <span className="font-mono">{shortSha(status.deployed_sha)}</span>)
              </p>
            )}
            <div className="mt-2 flex gap-6 text-xs text-gray-500">
              <span title="The commit this fleet's Mission Control container was built from.">
                MC image: <span className="font-mono">{shortSha(status.deployed_sha)}</span>
              </span>
              <span title="The most recent main commit touching mission-control/** — the only subtree that rebuilds the MC image. Not raw core HEAD.">
                Latest MC-affecting core: <span className="font-mono">{shortSha(status.latest_sha)}</span>
              </span>
              {status.checked_at && (
                <span>Checked {isFrozen ? new Date(status.checked_at).toLocaleString() : relativeTime(status.checked_at)}</span>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-400">
              These SHAs track only this fleet's Mission Control image (rebuilt when{' '}
              <span className="font-mono">mission-control/**</span> changes on core main). &ldquo;Up to date&rdquo; means
              the MC image already contains the latest MC-affecting core commit in its history — the two SHAs can
              differ even when up to date (e.g. the deployed image is a later merge commit). It does NOT reflect the
              agent-host runtime — see &ldquo;Agent runtime&rdquo; below.
            </p>
            <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
              <span>
                Agent runtime:{' '}
                {agentRuntimeUnavailable ? (
                  <span className="text-gray-400">unknown (unreachable)</span>
                ) : (
                  <span className="font-mono">{shortSha(agentRuntimeSha ?? undefined)}</span>
                )}
              </span>
              <p className="mt-1 text-gray-400">
                The core commit this fleet's agents (hermes containers) are actually running — auto-deployed, so it
                can legitimately differ from the operator-gated MC image above.
              </p>
            </div>
          </div>

          {/* Changelog — what this brings. */}
          {status.update_available && status.changelog && status.changelog.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <h2 className="text-sm font-semibold text-gray-700">What this brings</h2>
              <ul className="mt-2 space-y-1.5">
                {status.changelog.map(entry => (
                  <li key={entry.sha} className="text-sm text-gray-700 flex gap-2">
                    <span className="font-mono text-xs text-gray-400 shrink-0 mt-0.5">{shortSha(entry.sha)}</span>
                    <span>{entry.subject}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Dormant capabilities warning. */}
          {status.dormant_capabilities && status.dormant_capabilities.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <p className="font-semibold">⚠ Dormant capabilities</p>
              <p className="mt-1 text-xs">
                This fleet is missing required setup for: {status.dormant_capabilities.join(', ')}. Applying a
                core update alone may not resolve this — see the console's fleet sync-health panel.
              </p>
            </div>
          )}

          {/* Accept & apply — fleet_owner only, same primitive as the
              (now-linked-to-here) Fleet-Health card. */}
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold text-gray-700">Accept & apply core update</h2>
                <p className="text-xs text-gray-500 mt-1 max-w-xl">
                  Redeploys this fleet's agents AND its Mission Control from core{' '}
                  <span className="font-mono">main</span> on demand, via the console — recomposes the fleet's
                  SOUL/skills, refreshes its base image from the latest core, and updates this fleet's MC container.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { void handleApply() }}
                disabled={!isOwner || applying || isFrozen}
                title={isFrozen ? 'Deployments are frozen for this fleet' : !isOwner ? 'Fleet owner required to apply a core update' : undefined}
                className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors shrink-0"
              >
                {applying ? 'Dispatching…' : 'Accept & apply core update'}
              </button>
            </div>

            {!isOwner && (
              <p className="mt-2 text-xs text-gray-400">Only the fleet owner can apply a core update.</p>
            )}

            {applyResult && (
              <div className="mt-3 space-y-2">
                {applyResult.agent_host.held ? (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Agent-host deploy held{applyResult.agent_host.detail ? `: ${applyResult.agent_host.detail}` : ''}
                  </div>
                ) : applyResult.agent_host.dispatched ? (
                  <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
                    Agent update dispatched — the fleet's agents are redeploying from core main.
                  </div>
                ) : (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {applyResult.agent_host.unauthorized
                      ? "Agent-host deploy needs authorization — contact an admin. Mission Control's own update is unaffected."
                      : `Agent update could not be dispatched: ${applyResult.agent_host.detail}`}
                  </div>
                )}
                {applyResult.mission_control.held ? (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Mission Control deploy held{applyResult.mission_control.detail ? `: ${applyResult.mission_control.detail}` : ''}
                  </div>
                ) : applyResult.mission_control.dispatched ? (
                  <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
                    Mission Control redeploy dispatched — this fleet's MC is updating too.
                  </div>
                ) : (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {applyResult.mission_control.unauthorized
                      ? 'Mission Control deploy needs authorization — contact an admin. The agents are updating regardless.'
                      : `Mission Control could not be redeployed: ${applyResult.mission_control.detail}`}
                  </div>
                )}
              </div>
            )}

            {applyError && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {applyError}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
